/**
 * Required Documents Section
 * 
 * Displays the required business documents for vendor verification:
 * - W-9 Form
 * - Business Registration (optional, controlled by feature flag)
 * - CGL Insurance Certificate
 */

import React, { useCallback, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileCheck, AlertCircle } from 'lucide-react';
import { BusinessDocumentUploadCard } from './BusinessDocumentUploadCard';
import { 
  useBusinessDocuments, 
  checkAllDocumentsUploaded, 
  getUploadedDocumentCount, 
  getTotalRequiredDocumentCount,
  type BusinessDocumentType 
} from '@/hooks/useBusinessDocuments';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';

interface RequiredDocumentsSectionProps {
  vendorProfileId: string | null;
  isVerified: boolean;
  documents: {
    w9_document_url: string | null;
    w9_uploaded_at: string | null;
    business_registration_url: string | null;
    business_registration_uploaded_at: string | null;
    cgl_insurance_url: string | null;
    cgl_insurance_uploaded_at: string | null;
    cgl_insurance_expiry_date: string | null;
  };
  onDocumentChange: () => void;
  disabled?: boolean;
}

export function RequiredDocumentsSection({
  vendorProfileId,
  isVerified,
  documents,
  onDocumentChange,
  disabled = false,
}: RequiredDocumentsSectionProps) {
  const [requireBusinessRegistration, setRequireBusinessRegistration] = useState(true);
  const [requireW9, setRequireW9] = useState(true);
  
  const { uploadDocument, removeDocument, getDocumentUrl, isUploading } = useBusinessDocuments(
    vendorProfileId,
    isVerified
  );

  // Check feature flags on mount
  useEffect(() => {
    const checkFeatureFlags = async () => {
      const [isBusinessRegRequired, isW9Required] = await Promise.all([
        featureFlags.isEnabled(FEATURE_FLAGS.REQUIRE_BUSINESS_REGISTRATION),
        featureFlags.isEnabled(FEATURE_FLAGS.REQUIRE_W9),
      ]);
      setRequireBusinessRegistration(isBusinessRegRequired);
      setRequireW9(isW9Required);
    };
    checkFeatureFlags();
  }, []);

  const totalRequired = getTotalRequiredDocumentCount(requireBusinessRegistration, requireW9);
  const uploadedCount = getUploadedDocumentCount(documents, requireBusinessRegistration, requireW9);
  const allUploaded = checkAllDocumentsUploaded(documents, requireBusinessRegistration, requireW9);
  const progressPercentage = (uploadedCount / totalRequired) * 100;

  const handleUpload = useCallback(async (
    type: BusinessDocumentType,
    file: File,
    expiryDate?: Date
  ) => {
    const success = await uploadDocument(type, file, expiryDate);
    if (success) {
      onDocumentChange();
    }
    return success;
  }, [uploadDocument, onDocumentChange]);

  const handleRemove = useCallback(async (
    type: BusinessDocumentType,
    currentUrl: string | null
  ) => {
    const success = await removeDocument(type, currentUrl);
    if (success) {
      onDocumentChange();
    }
    return success;
  }, [removeDocument, onDocumentChange]);

  const handleView = useCallback(async (storagePath: string | null) => {
    if (!storagePath) return;
    const url = await getDocumentUrl(storagePath);
    if (url) {
      window.open(url, '_blank');
    }
  }, [getDocumentUrl]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCheck className="h-5 w-5" />
          Required Business Documents
        </CardTitle>
        <CardDescription>
          Upload the following documents to request Basic Verification. All three documents are required.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {uploadedCount} of {totalRequired} documents uploaded
            </span>
            {allUploaded && (
              <span className="text-green-600 font-medium flex items-center gap-1">
                <FileCheck className="h-4 w-4" />
                All documents uploaded
              </span>
            )}
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Document upload cards */}
        <div className={`grid grid-cols-1 gap-4 ${(requireBusinessRegistration && requireW9) ? 'md:grid-cols-3' : (!requireBusinessRegistration && !requireW9) ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
          {requireW9 && (
            <BusinessDocumentUploadCard
              documentType="w9"
              title="W-9 Form"
              description="IRS Form W-9 (Request for Taxpayer Identification Number)"
              existingDocumentUrl={documents.w9_document_url}
              uploadedAt={documents.w9_uploaded_at}
              isUploading={isUploading}
              disabled={disabled}
              onUpload={(file) => handleUpload('w9', file)}
              onRemove={() => handleRemove('w9', documents.w9_document_url)}
              onView={() => handleView(documents.w9_document_url)}
            />
          )}

          {requireBusinessRegistration && (
            <BusinessDocumentUploadCard
              documentType="business_registration"
              title="Business Registration"
              description="State or local business registration or license"
              existingDocumentUrl={documents.business_registration_url}
              uploadedAt={documents.business_registration_uploaded_at}
              isUploading={isUploading}
              disabled={disabled}
              onUpload={(file) => handleUpload('business_registration', file)}
              onRemove={() => handleRemove('business_registration', documents.business_registration_url)}
              onView={() => handleView(documents.business_registration_url)}
            />
          )}

          <BusinessDocumentUploadCard
            documentType="cgl_insurance"
            title="CGL Insurance Certificate"
            description="Commercial General Liability insurance certificate. $1M/$2M limits required."
            existingDocumentUrl={documents.cgl_insurance_url}
            uploadedAt={documents.cgl_insurance_uploaded_at}
            expiryDate={documents.cgl_insurance_expiry_date}
            requiresExpiryDate={true}
            isUploading={isUploading}
            disabled={disabled}
            onUpload={(file, expiryDate) => handleUpload('cgl_insurance', file, expiryDate)}
            onRemove={() => handleRemove('cgl_insurance', documents.cgl_insurance_url)}
            onView={() => handleView(documents.cgl_insurance_url)}
          />
        </div>

        {/* Helper message when documents are missing */}
        {!allUploaded && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Upload all required documents above to enable the "Request Basic Verification" button below.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export { checkAllDocumentsUploaded, getUploadedDocumentCount, getTotalRequiredDocumentCount };
